import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, mergeMap, of, Observable, switchMap, throwError } from 'rxjs';
import { PssAuthService, PssSession } from './pss-auth.service';

const AUTH_RETRY_HEADER = 'X-Auth-Retry';

function isAuthFailureText(text: string): boolean {
  const msg = text.toLowerCase();
  const mentionsAuth =
    msg.includes('access token') ||
    msg.includes('accesstoken') ||
    msg.includes('authenticationtoken') ||
    msg.includes('authentication token') ||
    msg.includes('authorization') ||
    msg.includes('authorisation') ||
    msg.includes('unauthori');
  const mentionsFailure =
    msg.includes('invalid') ||
    msg.includes('expired') ||
    msg.includes('missing') ||
    msg.includes('null') ||
    msg.includes('not found');

  return mentionsAuth && mentionsFailure;
}

function hasAuthFailureInBody(body: unknown): boolean {
  if (typeof body === 'string') {
    return isAuthFailureText(body);
  }

  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    const candidates = [obj['error'], obj['errorMessage'], obj['message'], obj['detail']]
      .filter((v): v is string => typeof v === 'string');

    if (candidates.some(isAuthFailureText)) return true;

    try {
      return isAuthFailureText(JSON.stringify(body));
    } catch {
      return false;
    }
  }

  return false;
}

function cloneWithFreshToken(req: HttpRequest<unknown>, session: PssSession): HttpRequest<unknown> {
  const newToken = session.accessToken;

  // /pss/ routes — accessToken embedded as a URL query param
  if (req.url.includes('/pss/')) {
    const updatedUrl = req.url.replace(
      /([?&]accessToken=)[^&]*/,
      `$1${encodeURIComponent(newToken)}`
    );
    return req.clone({ url: updatedUrl });
  }

  // /api/item/activate — accessToken in POST JSON body
  if (req.body && typeof req.body === 'object' && 'accessToken' in (req.body as object)) {
    return req.clone({ body: { ...(req.body as object), accessToken: newToken } });
  }

  return req;
}

function retryAfterReauth(
  auth: PssAuthService,
  interceptedReq: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError: unknown
): Observable<HttpEvent<unknown>> {
  // Force a fresh login regardless of cache, then retry once
  auth.lastAuthTime = 0;
  return auth.ensureFreshSession().pipe(
    switchMap(session => next(cloneWithFreshToken(interceptedReq, session).clone({ setHeaders: { [AUTH_RETRY_HEADER]: '1' } }))),
    catchError(() => throwError(() => originalError))
  );
}

export const authInterceptorFn: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const auth = inject(PssAuthService);

  // Attach user identity header for /api/ calls requiring account-based access.
  let interceptedReq = req;
  if (req.url.includes('/api/') && !req.headers.has('X-User-Id')) {
    const userId = auth?.currentSession?.userId;
    if (userId) {
      interceptedReq = interceptedReq.clone({ setHeaders: { 'X-User-Id': userId } });
    }
  }

  const isPssRoute = interceptedReq.url.includes('/pss/');
  const isItemActivate = interceptedReq.url.includes('/api/item/activate');
  const isTrackedRoute = isPssRoute || interceptedReq.url.includes('/api/');

  // Skip proactive auth for already-retried requests or non-tracked routes
  if (!isTrackedRoute || interceptedReq.headers.has(AUTH_RETRY_HEADER)) {
    return next(interceptedReq);
  }

  // For PSS routes and item/activate: proactively get a fresh token BEFORE sending
  if (isPssRoute || isItemActivate) {
    return auth.ensureFreshSession().pipe(
      switchMap(session => {
        const withToken = cloneWithFreshToken(interceptedReq, session);
        return next(withToken).pipe(
          mergeMap(event => {
            // Safety net: PSS sometimes returns 200 with auth error text in body
            if (!(event instanceof HttpResponse) || !hasAuthFailureInBody(event.body)) {
              return of(event);
            }
            const bodyAuthError = new HttpErrorResponse({
              status: 401,
              statusText: 'Unauthorized',
              url: event.url ?? interceptedReq.url,
              error: { error: 'access token expired', body: event.body },
            });
            return retryAfterReauth(auth, interceptedReq, next, bodyAuthError);
          }),
          catchError(err => {
            if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
            const isAuthErr =
              err.status === 401 ||
              isAuthFailureText((err.error as { error?: string } | null)?.error ?? err.message ?? '');
            if (!isAuthErr) return throwError(() => err);
            return retryAfterReauth(auth, interceptedReq, next, err);
          })
        );
      }),
      catchError(err => throwError(() => err))
    );
  }

  // Other /api/ routes — forward, with auth failure safety net
  // Note: body scanning is intentionally omitted here because /api/ responses
  // can contain large embedded XML (e.g. /api/device shipXml/userDataXml) which
  // would false-positive on accessToken + null word matches. PSS auth errors on
  // these routes come back as real HTTP 400/401 errors, not 200-with-error-body.
  return next(interceptedReq).pipe(
    catchError(err => {
      if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
      const isAuthErr =
        err.status === 401 ||
        (err.status === 400 && isAuthFailureText((err.error as { error?: string } | null)?.error ?? ''));
      if (!isAuthErr) return throwError(() => err);
      return retryAfterReauth(auth, interceptedReq, next, err);
    })
  );
};
